import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    kindLabel, channelLabel, MessageChannels, LinkTypes, ALL_MESSAGE_CHANNELS
} from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Checkbox, ErrorNote, toast
} from './ui';

/**
 * Handing a survey or listening link to the people who should answer it.
 *
 * One panel does all of it, because it is all one decision made in one sitting: which campaign,
 * whose link, how it travels, and who gets it. Splitting "create a link" from "send a link" onto
 * two screens would mean copying a URL between them, which is the manual step this replaces.
 *
 * Its own component rather than more of the responses page: that page answers "who has replied",
 * this one answers "who has been asked", and the two are read at different moments.
 */
interface Option {
    id: number;
    name: string;
}

interface BranchOption extends Option {
    stateId: number;
}

interface SendResult {
    sent: number;
    failed: number;
    messages: { employeeCode: string; channel: string; recipient: string; error: string | null }[];
    skipped: { employeeCode: string; channel: string; reason: string }[];
    problems: { line: number; employeeCode: string; reason: string }[];
    truncated: boolean;
}

const EMPTY_PERSON = {
    employeeCode: '', email: '', mobile: '', role: '', region: '', branch: '', state: ''
};

/** The columns a pasted list may name, shown as a starting point people can edit over. */
const CSV_PLACEHOLDER = 'EmployeeCode,Email,Mobile,Role,Region,State,Branch\n'
    + 'V10234,priya@example.com,9876543210,Technician,West,Maharashtra,Pune\n';

export function SendInvites({ kind }: { kind: 'Survey' | 'Listening' }) {
    const [formatId, setFormatId] = useState('');
    const [linkType, setLinkType] = useState<string>(LinkTypes.Employee);
    const [channels, setChannels] = useState<string[]>([MessageChannels.Email]);
    const [bulk, setBulk] = useState(false);
    const [person, setPerson] = useState(EMPTY_PERSON);
    const [csv, setCsv] = useState('');
    const [result, setResult] = useState<SendResult | null>(null);
    const [issued, setIssued] = useState<string | null>(null);
    const [error, setError] = useState<ApiError | null>(null);
    const queryClient = useQueryClient();

    const formats = useQuery({
        queryKey: ['interaction-formats', kind],
        queryFn: () => api.get<{ id: number; name: string }[]>(
            `/api/admin/interaction-formats?kind=${kind}`)
    });

    // One query per list, matching the one endpoint per table. Shared across both screens, so the
    // keys carry no kind - and React Query serves them from cache when the responses page has
    // already asked.
    const roles = useQuery({
        queryKey: ['org', 'roles'],
        queryFn: () => api.get<Option[]>('/api/admin/org/roles')
    });

    const regions = useQuery({
        queryKey: ['org', 'regions'],
        queryFn: () => api.get<Option[]>('/api/admin/org/regions')
    });

    const states = useQuery({
        queryKey: ['org', 'states'],
        queryFn: () => api.get<Option[]>('/api/admin/org/states')
    });

    // Asked for by state, so the list is never the ambiguous one: Chandigarh is a branch in five
    // states, and picking the wrong one is not something anybody could see afterwards.
    const stateId = states.data?.find((s) => s.name === person.state)?.id;

    const branches = useQuery({
        queryKey: ['org', 'branches', stateId ?? 0],
        queryFn: () => api.get<BranchOption[]>(`/api/admin/org/branches?stateId=${stateId}`),
        enabled: stateId !== undefined
    });

    const settled = () => {
        setError(null);
        void queryClient.invalidateQueries({ queryKey: ['interactions', kind] });
        void queryClient.invalidateQueries({ queryKey: ['interaction-messages', kind] });
    };

    const send = useMutation({
        mutationFn: () => api.post<SendResult>('/api/admin/interactions/send', {
            kind,
            formatId: Number(formatId),
            linkType,
            channels,
            ...(bulk ? { csv } : { recipients: [person] })
        }),
        onSuccess: (sent) => {
            setResult(sent);
            setIssued(null);
            settled();
            toast(sent.failed === 0
                ? `Sent ${sent.sent}.`
                : `Sent ${sent.sent}, ${sent.failed} did not go.`,
            sent.failed === 0 ? 'success' : 'error');
        },
        onError: (caught: ApiError) => { setError(caught); setResult(null); }
    });

    // Kept beside sending rather than replaced by it: somebody who wants to paste the link into
    // their own message, or read it out over the phone, still needs it.
    const issue = useMutation({
        mutationFn: () => api.post<{ interactionUrl: string }>('/api/admin/interactions/link', {
            ...person, formatId: Number(formatId)
        }),
        onSuccess: (created) => {
            setIssued(created.interactionUrl);
            setResult(null);
            settled();
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const toggleChannel = (channel: string, on: boolean) =>
        setChannels((current) => on
            ? [...current, channel]
            : current.filter((c) => c !== channel));

    const personal = linkType === LinkTypes.Employee;
    const busy = send.isPending || issue.isPending;

    return (
        <Card
            title={`Send a ${kindLabel(kind).toLowerCase()} out`}
            lede="Choose who it goes to and how it reaches them. Sending it twice to the same person
                  gives them the same link again, not a second one."
        >
            <form
                className="space-y-5"
                onSubmit={(e) => { e.preventDefault(); send.mutate(); }}
            >
                {error && !error.errors && <ErrorNote message={error.message} />}

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Which one" error={error?.fieldError('formatId')}>
                        <Select
                            value={formatId}
                            onChange={(e) => setFormatId(e.target.value)}
                            invalid={!!error?.fieldError('formatId')}
                        >
                            <option value="">Choose…</option>
                            {(formats.data ?? []).map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field
                        label="Which link"
                        hint={personal
                            ? 'Everyone gets their own, so you can see who has not replied.'
                            : 'One link for everybody. They type their own employee code, and '
                                + 'you cannot tell who has not answered.'}
                    >
                        <Select value={linkType} onChange={(e) => setLinkType(e.target.value)}>
                            <option value={LinkTypes.Employee}>A personal link each</option>
                            <option value={LinkTypes.Shared}>The shared link</option>
                        </Select>
                    </Field>
                </div>

                <Field label="How it reaches them" error={error?.fieldError('channels')}>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 pt-1">
                        {ALL_MESSAGE_CHANNELS.map((channel) => (
                            <Checkbox
                                key={channel}
                                label={channelLabel(channel)}
                                checked={channels.includes(channel)}
                                onChange={(on) => toggleChannel(channel, on)}
                            />
                        ))}
                    </div>
                </Field>

                <Field label="Who it goes to">
                    <Select
                        value={bulk ? 'list' : 'one'}
                        onChange={(e) => { setBulk(e.target.value === 'list'); setResult(null); }}
                    >
                        <option value="one">One person</option>
                        <option value="list">A list I paste or upload</option>
                    </Select>
                </Field>

                {bulk
                    ? (
                        <Field
                            label="The list"
                            hint="First line names the columns. Only EmployeeCode is required; add
                                  Email or Mobile for whichever way you are sending it. Up to 500
                                  rows at a time."
                            error={error?.fieldError('csv')}
                        >
                            <div className="space-y-2">
                                <TextArea
                                    rows={8}
                                    className="font-mono text-xs"
                                    placeholder={CSV_PLACEHOLDER}
                                    value={csv}
                                    onChange={(e) => setCsv(e.target.value)}
                                />
                                <input
                                    type="file"
                                    accept=".csv,text/csv,text/plain"
                                    className="block text-sm text-slate-600"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        // Read here rather than uploaded: it lands in the box above,
                                        // where a wrong file can be seen and corrected before it
                                        // becomes two hundred messages.
                                        if (file) setCsv(await file.text());
                                    }}
                                />
                            </div>
                        </Field>
                    )
                    : (
                        <div className="space-y-4">
                            <div className="grid gap-4 sm:grid-cols-3">
                                <Field
                                    label="Employee code"
                                    error={error?.fieldError('employeeCode')}
                                >
                                    <TextInput
                                        value={person.employeeCode}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, employeeCode: e.target.value }))}
                                        invalid={!!error?.fieldError('employeeCode')}
                                    />
                                </Field>

                                <Field
                                    label="Email address"
                                    hint={channels.includes(MessageChannels.Email)
                                        ? undefined
                                        : 'Not needed for how you are sending this.'}
                                >
                                    <TextInput
                                        type="email"
                                        value={person.email}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, email: e.target.value }))}
                                    />
                                </Field>

                                <Field
                                    label="Mobile number"
                                    hint={channels.some((c) => c !== MessageChannels.Email)
                                        ? 'With or without +91.'
                                        : 'Not needed for how you are sending this.'}
                                >
                                    <TextInput
                                        value={person.mobile}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, mobile: e.target.value }))}
                                    />
                                </Field>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-4">
                                <Field label="Role">
                                    <Select
                                        value={person.role}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, role: e.target.value }))}
                                    >
                                        <option value="">Choose…</option>
                                        {(roles.data ?? []).map((r) => (
                                            <option key={r.id} value={r.name}>{r.name}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Region">
                                    <Select
                                        value={person.region}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, region: e.target.value }))}
                                    >
                                        <option value="">Choose…</option>
                                        {(regions.data ?? []).map((r) => (
                                            <option key={r.id} value={r.name}>{r.name}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="State">
                                    <Select
                                        value={person.state}
                                        onChange={(e) => setPerson((p) => ({
                                            // The branch goes with the state it belonged to. Leaving
                                            // it selected attaches a branch to a state it is not in.
                                            ...p, state: e.target.value, branch: ''
                                        }))}
                                    >
                                        <option value="">Choose…</option>
                                        {(states.data ?? []).map((s) => (
                                            <option key={s.id} value={s.name}>{s.name}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field
                                    label="Branch"
                                    hint={person.state ? undefined : 'Pick a state first.'}
                                >
                                    <Select
                                        value={person.branch}
                                        disabled={!person.state}
                                        onChange={(e) =>
                                            setPerson((p) => ({ ...p, branch: e.target.value }))}
                                    >
                                        <option value="">
                                            {(branches.data ?? []).length === 0 && person.state
                                                ? 'No branches in that state'
                                                : 'Choose…'}
                                        </option>
                                        {(branches.data ?? []).map((b) => (
                                            <option key={b.id} value={b.name}>{b.name}</option>
                                        ))}
                                    </Select>
                                </Field>
                            </div>
                        </div>
                    )}

                <div className="flex flex-wrap gap-3">
                    <Button type="submit" busy={send.isPending} disabled={busy}>
                        {bulk ? 'Send to the list' : 'Send it'}
                    </Button>

                    {!bulk && (
                        <Button
                            type="button"
                            variant="secondary"
                            busy={issue.isPending}
                            disabled={busy}
                            onClick={() => issue.mutate()}
                        >
                            Just give me the link
                        </Button>
                    )}
                </div>
            </form>

            {issued && (
                <div className="mt-5">
                    <Field
                        label="Their link"
                        hint="This is the only time it can be shown — only a hash of it is stored.
                              Creating another link replaces this one."
                    >
                        <div className="flex gap-2">
                            <TextInput readOnly value={issued} />
                            <Button
                                variant="secondary"
                                onClick={async () => {
                                    await navigator.clipboard.writeText(issued);
                                    toast('Copied.');
                                }}
                            >
                                Copy
                            </Button>
                        </div>
                    </Field>
                </div>
            )}

            {result && <SendReport result={result} />}
        </Card>
    );
}

/**
 * What happened, in three separate lists, because they are three different things to do next.
 *
 * A failure is worth retrying. A skip means somebody is missing a phone number. A problem row means
 * the list itself needs fixing. Collapsing them into one count would leave every one of those
 * actions un-signposted.
 */
function SendReport({ result }: { result: SendResult }) {
    const failures = result.messages.filter((m) => m.error !== null);

    return (
        <div className="mt-6 space-y-4 border-t border-slate-200 pt-5">
            <p className="text-sm font-medium text-slate-700">
                {result.sent > 0 && `${result.sent} sent. `}
                {result.failed > 0 && `${result.failed} did not go. `}
                {result.sent === 0 && result.failed === 0 && 'Nothing was sent. '}
                {result.truncated && 'The list was longer than 500 rows and was cut off there. '}
            </p>

            {failures.length > 0 && (
                <ProblemList
                    title="These did not go"
                    rows={failures.map((f) => ({
                        who: `${f.employeeCode} · ${channelLabel(f.channel)} · ${f.recipient}`,
                        why: f.error ?? ''
                    }))}
                />
            )}

            {result.skipped.length > 0 && (
                <ProblemList
                    title="Nothing to send these on"
                    rows={result.skipped.map((s) => ({
                        who: `${s.employeeCode} · ${channelLabel(s.channel)}`,
                        why: s.reason
                    }))}
                />
            )}

            {result.problems.length > 0 && (
                <ProblemList
                    title="Rows that were left out"
                    rows={result.problems.map((p) => ({
                        who: p.employeeCode || `Line ${p.line}`,
                        why: p.reason
                    }))}
                />
            )}
        </div>
    );
}

function ProblemList({ title, rows }: { title: string; rows: { who: string; why: string }[] }) {
    return (
        <div className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {title} ({rows.length})
            </h3>
            <ul className="space-y-1 text-sm">
                {rows.map((row, index) => (
                    <li key={index} className="flex flex-wrap gap-x-2">
                        <span className="font-medium text-slate-700">{row.who}</span>
                        <span className="text-slate-500">{row.why}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
